import FVector from '@data/geometry/FVector';
import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import {
    BoxDesignState,
    ConvexHullDesignState,
    DesignSchemeSetType,
    FloorBasedDesignState,
    LayerKind,
    OrientedVolumeDesignState,
    RendererBaseConfig,
    SchemeKey,
} from '@visualizer/types';
import Layer from 'Simulations/Layer';
import { DesignScheme } from './DesignSchemes/designScheme';
import { FloorBased } from './DesignSchemes/FloorBased/FloorBased';
import { Box } from './DesignSchemes/SingleBuilding/Box';
import { ConvexHull } from './DesignSchemes/SingleBuilding/ConvexHull';
import { OrientedVolume } from './DesignSchemes/SingleBuilding/OrientedVolume';

type FootprintEntry = { id: string; points: FVector[] };

export class DesignSchemeSet {
    private id: string;
    private name: string;
    private type: DesignSchemeSetType;
    private designSchemes: DesignScheme[] = [];
    private footprints: FootprintEntry[] = [];
    private visibility: boolean = true;

    constructor(name: string, type: DesignSchemeSetType) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.type = type;
    }

    // =====================================
    // LIFECYCLE
    // =====================================

    update(pipelineResult: PipelineExecutionResult, layerId: string): void {
        if (this.visibility) this.designSchemes.forEach((ds) => ds.updateSensors(pipelineResult, layerId));
    }

    // =====================================
    // GETTERS & SETTERS
    // =====================================

    getID() {
        return this.id;
    }

    setID(id: string) {
        this.id = id;
    }

    getName() {
        return this.name;
    }

    getType() {
        return this.type;
    }

    getDesignSchemes(): DesignScheme[] {
        return this.designSchemes;
    }

    getDesignSchemeByID(schemeID: string) {
        return this.designSchemes.find((ds) => ds.getID() === schemeID);
    }

    getDesignSchemesByFootprint(footprintId: string): DesignScheme[] {
        return this.designSchemes.filter((ds) => ds.getFootprintId() === footprintId);
    }

    getFootprints(): FootprintEntry[] {
        return this.footprints;
    }

    getFootprintById(fpId: string) {
        return this.footprints.find((fp) => fp.id === fpId);
    }

    isVisible(): boolean {
        return this.visibility;
    }

    setvisibility(visible: boolean): void {
        this.visibility = visible;

        this.designSchemes.forEach((ds) => {
            ds.update();
        });
    }

    // =====================================
    // ADD FUNCTIONS
    // =====================================

    addDesignScheme(fpId: string, schemeKey: SchemeKey, rendererConfig: RendererBaseConfig, linkedLayers?: Map<LayerKind, Layer>) {
        const footprint = this.footprints.find((fp) => fp.id === fpId) as FootprintEntry;
        const scheme = this.makeSchemeInstance(schemeKey, footprint?.points, footprint?.id, rendererConfig, linkedLayers);
        this.designSchemes.push(scheme);

        return scheme;
    }

    addFootprint(points: FVector[], fpId?: string) {
        const entry = { id: fpId ?? crypto.randomUUID(), points };
        this.footprints.push(entry);

        return entry;
    }

    // =====================================
    // REMOVE FUNCTIONS
    // =====================================
    removeDesignScheme(schemeID: string) {
        this.designSchemes = this.designSchemes.filter((ds) => ds.getID() !== schemeID);
    }

    removeFootprint(fpId: string) {
        // Find the index in the array:
        const index = this.footprints.findIndex((fp) => fp.id === fpId);
        if (index === -1) return; // not found

        // Remove schemes linked to this footprint in runtime:
        this.designSchemes = this.designSchemes.filter((ds) => ds.getFootprintId() !== fpId);

        // Remove from local footprints array:
        this.footprints.splice(index, 1);
    }

    // =====================================
    // HELPERS
    // =====================================

    toString(): string {
        switch (this.type) {
            case DesignSchemeSetType.FULL_SITE:
                return 'DesignScheme Set with Analysis Area Points';
            case DesignSchemeSetType.HAND_DRAWN:
                return 'DesignScheme Set with Custom Sectors';
            case DesignSchemeSetType.DATA_BASED:
                return 'DesignScheme Set with Data-Based Sectors';
            default:
                return 'Unknown DesignScheme Set Type';
        }
    }

    // Helper:
    makeSchemeInstance(
        key: SchemeKey,
        polygon: FVector[],
        footprintId: string,
        rendererConfig: RendererBaseConfig,
        linkedLayers?: Map<LayerKind, Layer>
    ): DesignScheme {
        switch (key) {
            case SchemeKey.FLOOR_BASED:
                return new FloorBased(polygon, footprintId, this, rendererConfig as FloorBasedDesignState, linkedLayers);
            case SchemeKey.CONVEX_HULL:
                return new ConvexHull(polygon, footprintId, this, rendererConfig as ConvexHullDesignState, linkedLayers);
            case SchemeKey.ORIENTED_VOLUME:
                return new OrientedVolume(polygon, footprintId, this, rendererConfig as OrientedVolumeDesignState, linkedLayers);
            case SchemeKey.BOX:
                return new Box(polygon, footprintId, this, rendererConfig as BoxDesignState, linkedLayers);
            default:
                throw new Error(`Unsupported scheme key: ${key as string}`);
        }
    }
}
